import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd, { getOrganizationSchema, getWebsiteSchema, getWebPageSchema } from '@/components/feature/JsonLd';
import useScrollToTop from '@/hooks/useScrollToTop';
import { movies } from '@/mocks/movies';
import { siteInfo } from '@/mocks/siteInfo';
import HeroBanner from './components/HeroBanner';
import CategoryTabs from './components/CategoryTabs';
import MovieSection from './components/MovieSection';

export default function Home() {
  useScrollToTop();
  const [activeCategory, setActiveCategory] = useState('all');

  const hotMovies = useMemo(() => movies.filter(m => m.isHot).slice(0, 6), []);
  const newMovies = useMemo(() => movies.filter(m => m.isNew).slice(0, 6), []);
  const dramaMovies = useMemo(() => movies.filter(m => m.categorySlug === 'drama').slice(0, 6), []);
  const animeMovies = useMemo(() => movies.filter(m => m.categorySlug === 'anime').slice(0, 6), []);

  const filteredMovies = useMemo(() => {
    if (activeCategory === 'all') return movies;
    return movies.filter(m => m.categorySlug === activeCategory);
  }, [activeCategory]);

  return (
    <>
      <JsonLd {...getOrganizationSchema()} />
      <JsonLd {...getWebsiteSchema()} />
      <JsonLd {...getWebPageSchema(
        `${siteInfo.name} | 4K电影超清影视免费看 | 4K电视剧高清画质在线`,
        siteInfo.description,
        siteInfo.domain,
      )} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <HeroBanner />

          <MovieSection
            title="热门推荐"
            subtitle="全网热播，口碑爆棚的精选好片"
            movies={hotMovies}
            viewAllLink="/category/movie"
          />

          <CategoryTabs active={activeCategory} onSelect={setActiveCategory} />

          <section className="pb-10 md:pb-14">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
                {filteredMovies.map((movie) => (
                  <Link
                    key={movie.id}
                    to={`/detail/${movie.id}`}
                    className="group block"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  >
                    <div className="relative aspect-[2/3] rounded-lg overflow-hidden mb-2.5 bg-background-200">
                      <img
                        src={movie.poster}
                        alt={movie.title}
                        title={`${movie.title} - 4K电影高清画质在线观看`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-background-950/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                      <div className="absolute top-2 left-2 flex items-center gap-1">
                        <span className="px-1.5 py-0.5 text-[10px] font-medium bg-primary-500 text-background-50 rounded whitespace-nowrap">
                          {movie.quality}
                        </span>
                        {movie.isNew && (
                          <span className="px-1.5 py-0.5 text-[10px] font-medium bg-accent-500 text-background-50 rounded whitespace-nowrap">
                            NEW
                          </span>
                        )}
                      </div>

                      <div className="absolute top-2 right-2">
                        <span className="flex items-center gap-0.5 text-xs font-medium text-background-50 bg-background-950/60 px-1.5 py-0.5 rounded">
                          <i className="ri-star-fill text-accent-400 text-[10px]"></i>
                          {movie.rating}
                        </span>
                      </div>

                      {(movie.episodeTotal && movie.episodeCurrent && movie.episodeCurrent < movie.episodeTotal) && (
                        <div className="absolute bottom-2 left-2">
                          <span className="px-1.5 py-0.5 text-[10px] bg-background-950/70 text-primary-400 rounded whitespace-nowrap">
                            更新至{movie.episodeCurrent}集
                          </span>
                        </div>
                      )}

                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500/90 text-background-50">
                          <i className="ri-play-fill text-xl"></i>
                        </div>
                      </div>
                    </div>

                    <h3 className="text-sm font-medium text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors">
                      {movie.title}
                    </h3>
                    <div className="flex items-center gap-2 mt-1 text-xs text-foreground-500">
                      <span>{movie.year}</span>
                      <span>|</span>
                      <span className="line-clamp-1">{movie.genre.slice(0, 2).join(' · ')}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          <MovieSection
            title="最新上线"
            subtitle="抢先观看最新热门影片"
            movies={newMovies}
            viewAllLink="/category/movie"
          />

          <MovieSection
            title="热播剧集"
            subtitle="追剧达人的最佳选择"
            movies={dramaMovies}
            viewAllLink="/category/drama"
          />

          <MovieSection
            title="动漫番剧"
            subtitle="热血与治愈的二次元世界"
            movies={animeMovies}
            viewAllLink="/category/anime"
          />

          <section className="py-10 md:py-14 bg-background-100">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6 text-center">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-3">
                畅享4K超清，尽在{siteInfo.name}
              </h2>
              <p className="text-foreground-600 max-w-2xl mx-auto mb-8 text-sm md:text-base leading-relaxed">
                {siteInfo.slogan}。我们致力于为影迷提供极致的观影体验，海量4K电影、超清电视剧、热门动漫、综艺节目，随时随地免费观看，不缓冲不卡顿。
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { icon: 'ri-4k-line', label: '4K超清画质', desc: '极致视觉享受' },
                  { icon: 'ri-thunderstorms-line', label: '极速加载', desc: 'CDN全球加速' },
                  { icon: 'ri-film-line', label: '海量片库', desc: '10万+影视资源' },
                  { icon: 'ri-smartphone-line', label: '多端同步', desc: '随时随地观看' },
                ].map((item) => (
                  <div key={item.label} className="flex flex-col items-center gap-2">
                    <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-2xl bg-primary-100 text-primary-500">
                      <i className={`${item.icon} text-2xl`}></i>
                    </div>
                    <h4 className="text-sm font-semibold text-foreground-900">{item.label}</h4>
                    <p className="text-xs text-foreground-500">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}